import { IPrivateNote, PrivateNote } from '@app/models/PrivateNote'
import { List, Record } from 'immutable'
import { Address, IAddress } from './Address'
import { AssociatedContact, IAssociatedContact } from './AssociatedContact'
import { AssociatedGroup, IAssociatedGroup } from './AssociatedGroup'
import { AssociatedProperty, IAssociatedProperty } from './AssociatedProperty'
import { Attachment, IAttachment } from './Attachment'
import { Company, ICompany } from './Company'
import { EmailAddress, IEmailAddress } from './EmailAddress'
import { FeedItem, IFeedItem } from './FeedItem'
import { IPhoneNumber, PhoneNumber } from './PhoneNumber'
import { ISocialConnection, SocialConnection } from './SocialConnection'
import { IUser, User } from './User'

export interface IContact {
  readonly addresses: List<IAddress>
  readonly associated: List<IAssociatedContact>
  readonly birthday?: number
  readonly company?: ICompany
  readonly creator: string
  readonly emailAddresses: List<IEmailAddress>
  readonly firstName: string
  readonly fullName: string
  readonly groups: List<IAssociatedGroup>
  readonly hasStarred: List<string>
  readonly id: string
  readonly isCompany: boolean
  readonly lastName: string
  readonly leads: List<IFeedItem>
  readonly note: string
  readonly phoneNumbers: List<IPhoneNumber>
  readonly privateNote: IPrivateNote
  readonly profileImage?: IAttachment
  readonly profileImageId: string
  readonly properties: List<IAssociatedProperty>
  readonly social: List<ISocialConnection>
  readonly title: string
  readonly url: string
  readonly user?: IUser
}

const ContactRecord = Record({
  addresses: List<Address>(),
  associated: List<AssociatedContact>(),
  birthday: undefined,
  company: undefined,
  creator: '',
  emailAddresses: List<EmailAddress>(),
  firstName: '',
  fullName: '',
  groups: List<AssociatedGroup>(),
  hasStarred: List<string>(),
  id: '',
  isCompany: false,
  lastName: '',
  leads: List<FeedItem>(),
  note: '',
  phoneNumbers: List<PhoneNumber>(),
  privateNote: new PrivateNote(),
  profileImage: undefined,
  profileImageId: '',
  properties: List<AssociatedProperty>(),
  social: List<SocialConnection>(),
  title: '',
  url: '',
  user: undefined,
})

export class Contact extends ContactRecord implements IContact {
  public static fromJS(initialState?: Partial<IContact>) {
    if (!initialState) {
      return new Contact()
    }

    const {
      addresses,
      associated,
      company,
      emailAddresses,
      groups,
      hasStarred,
      leads,
      privateNote,
      phoneNumbers,
      profileImage,
      properties,
      social,
      user,
      ...rest
    } = initialState

    return new Contact({
      addresses: addresses
        ? List(addresses.map((a) => Address.fromJS(a)))
        : List<Address>(),
      associated: associated
        ? List(associated.map((a) => AssociatedContact.fromJS(a)))
        : List<AssociatedContact>(),
      company: company ? Company.fromJS(company) : undefined,
      emailAddresses: emailAddresses
        ? List(emailAddresses.map((e) => EmailAddress.fromJS(e)))
        : List<EmailAddress>(),
      groups: groups
        ? List(groups.map((g) => AssociatedGroup.fromJS(g)))
        : List<AssociatedGroup>(),
      hasStarred: List(hasStarred || []),
      leads: leads
        ? List(leads.map((a) => FeedItem.fromJS(a)))
        : List<FeedItem>(),
      phoneNumbers: phoneNumbers
        ? List(phoneNumbers.map((p) => PhoneNumber.fromJS(p)))
        : List<PhoneNumber>(),
      privateNote: privateNote
        ? PrivateNote.fromJS(privateNote)
        : new PrivateNote(),
      profileImage: profileImage ? Attachment.fromJS(profileImage) : undefined,
      properties: properties
        ? List(properties.map((a) => AssociatedProperty.fromJS(a)))
        : List<AssociatedProperty>(),
      social: social
        ? List(social.map((s) => SocialConnection.fromJS(s)))
        : List<SocialConnection>(),
      user: user ? User.fromJS(user) : undefined,
      ...rest,
    })
  }

  public readonly addresses: List<Address>
  public readonly associated: List<AssociatedContact>
  public readonly birthday?: number
  public readonly company?: Company
  public readonly creator: string
  public readonly emailAddresses: List<EmailAddress>
  public readonly firstName: string
  public readonly fullName: string
  public readonly groups: List<AssociatedGroup>
  public readonly hasStarred: List<string>
  public readonly id: string
  public readonly isCompany: boolean
  public readonly lastName: string
  public readonly leads: List<FeedItem>
  public readonly note: string
  public readonly phoneNumbers: List<PhoneNumber>
  public readonly privateNote: PrivateNote
  public readonly profileImage?: Attachment
  public readonly profileImageId: string
  public readonly properties: List<AssociatedProperty>
  public readonly social: List<SocialConnection>
  public readonly title: string
  public readonly url: string
  public readonly user: User

  constructor(initialState?: Partial<Contact>) {
    initialState ? super(initialState) : super()
  }
}
